import { ReactElement, MutableRefObject } from 'react';
import { School } from '@common-types';
import { CoreBox, CoreTypography } from '@youscience/core';

import StudyAbroadIcon from '@images/ExtraProgramsIcons/study-abroad.svg?react';
import DualEnrollmentIcon from '@images/ExtraProgramsIcons/dual-enrollment.svg?react';
import LifeExperienceIcon from '@images/ExtraProgramsIcons/life-experience.svg?react';
import AdvancePlacementIcon from '@images/ExtraProgramsIcons/advance-placement.svg?react';

import ROTCIcon from '@images/ExtraProgramsIcons/rotc.svg?react';
import ArmyROTCIcon from '@images/ExtraProgramsIcons/army-rotc.svg?react';
import NavyROTCIcon from '@images/ExtraProgramsIcons/navy-rotc.svg?react';
import AirForceROTCIcon from '@images/ExtraProgramsIcons/air-force-rotc.svg?react';

import RemedialServiceIcon from '@images/ExtraProgramsIcons/remedial-service.svg?react';
import AcademicServiceIcon from '@images/ExtraProgramsIcons/academic-service.svg?react';
import EmploymentServiceIcon from '@images/ExtraProgramsIcons/employment-service.svg?react';
import PlacementServiceIcon from '@images/ExtraProgramsIcons/placement-service.svg?react';
import OnCampusDayIcon from '@images/ExtraProgramsIcons/on-campus-day.svg?react';

import {
  sxStyles,
  StyledSchoolOffersWrap,
  StyledSchoolOffersInner,
  StyledProgramIconWrap,
} from './SchoolOffers.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { linkOffset, SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

interface SchoolOffersProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  school: School;
  hasData: boolean;
}

const ProgramItem = (title: string, icon: ReactElement) => (
  <CoreBox sx={sxStyles.programContainer}>
    <StyledProgramIconWrap>{icon}</StyledProgramIconWrap>

    <CoreTypography variant="body2">{title}</CoreTypography>
  </CoreBox>
);

export const SchoolOffers = (props: SchoolOffersProps) => {
  const {
    scrollToRef,
    school: { characteristics },
    hasData,
  } = props;

  if (!characteristics) {
    return null;
  }
  const hasSpecialPrograms =
    characteristics.hasStudyAbroad ||
    characteristics.acceptsAPCredit ||
    characteristics.acceptsCreditForLifeExperience ||
    characteristics.acceptsDualEnrollment ||
    characteristics.acceptsNoCredit;
  const hasExtracurriculars =
    characteristics.hasAirForceROTC ||
    characteristics.hasArmyROTC ||
    characteristics.hasROTC ||
    characteristics.hasNavyROTC;
  const hasServices =
    characteristics.hasRemedialService ||
    characteristics.hasCounselingService ||
    characteristics.hasEmploymentService ||
    characteristics.hasPlacementService ||
    characteristics.hasDayCareService;

  if (!hasData) return null;

  return (
    <CoreBox sx={sxStyles.schoolOffersMainContainer}>
      <SectionLinkOffset id={PageSectionIds['extracurricular-activities']} ref={scrollToRef} sx={linkOffset(300)} />

      <CoreTypography sx={sxStyles.schoolOffersHeaderTitle} variant="h4" component="h3">
        Extracurriculars, services, and special programs
      </CoreTypography>

      <StyledSchoolOffersWrap>
        <CoreTypography sx={sxStyles.schoolOffersTitle} variant="h5" component="h4">
          College offers
        </CoreTypography>

        <StyledSchoolOffersInner>
          <CoreBox>
            {hasSpecialPrograms && (
              <>
                <CoreTypography sx={sxStyles.specialProgramsTitle} variant="overline" component="h5">
                  Special Programs
                </CoreTypography>
                {characteristics.hasStudyAbroad && ProgramItem('Study abroad', <StudyAbroadIcon />)}
                {characteristics.acceptsDualEnrollment && ProgramItem('Dual enrollment', <DualEnrollmentIcon />)}
                {characteristics.acceptsCreditForLifeExperience &&
                  ProgramItem('Credit for life experience', <LifeExperienceIcon />)}
                {characteristics.acceptsAPCredit &&
                  ProgramItem('Advance placement (AP) credit', <AdvancePlacementIcon />)}
                {characteristics.acceptsNoCredit &&
                  ProgramItem(
                    'Institution does not accept dual, credit for life, or AP credits',
                    <AdvancePlacementIcon />,
                  )}
              </>
            )}

            {hasExtracurriculars && (
              <>
                <CoreTypography sx={sxStyles.extracurricularsTitle} variant="overline" component="h5">
                  Extracurriculars
                </CoreTypography>
                {characteristics.hasROTC && ProgramItem('ROTC', <ROTCIcon />)}
                {characteristics.hasArmyROTC && ProgramItem('ROTC - Army', <ArmyROTCIcon />)}
                {characteristics.hasNavyROTC && ProgramItem('ROTC - Navy', <NavyROTCIcon />)}
                {characteristics.hasAirForceROTC && ProgramItem('ROTC - Air Force', <AirForceROTCIcon />)}
              </>
            )}
          </CoreBox>

          {hasServices && (
            <CoreBox>
              <CoreTypography variant="overline" component="h5">
                Services
              </CoreTypography>

              {characteristics.hasRemedialService && ProgramItem('Remedial service', <RemedialServiceIcon />)}
              {characteristics.hasCounselingService &&
                ProgramItem('Academic/career counseling service', <AcademicServiceIcon />)}
              {characteristics.hasEmploymentService &&
                ProgramItem('Employment services for students', <EmploymentServiceIcon />)}
              {characteristics.hasPlacementService &&
                ProgramItem('Placement services for completers', <PlacementServiceIcon />)}
              {characteristics.hasROTC && ProgramItem('On-campus day for students’ children', <OnCampusDayIcon />)}
            </CoreBox>
          )}
        </StyledSchoolOffersInner>
      </StyledSchoolOffersWrap>
    </CoreBox>
  );
};
